/** Paths, not hashes, so share links read well; the API serves index.html for every path. */
import { useEffect, useState } from 'react';

export type Route =
  | { page: 'kola'; path: string } | { page: 'pricing' } | { page: 'review'; boardId?: string } | { page: 'members' } | { page: 'machines' } | { page: 'credits' } | { page: 'settings' } | { page: 'invite'; code: string };

/** Kola owns these product paths: its dashboard (as /boards), boards, admin and the storyboard lab. */
export function parseRoute(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean).map(decodeURIComponent);
  switch (parts[0]) {
    case undefined: case 'boards': return { page: 'kola', path: '/' };
    case 'b': return parts[1] ? { page: 'kola', path: `/b/${encodeURIComponent(parts[1])}` } : { page: 'kola', path: '/' };
    case 'admin': return { page: 'kola', path: '/admin' };
    case 'storyboard-lab': return { page: 'kola', path: '/storyboard-lab' };
    case 'pricing': return { page: 'pricing' };
    case 'review': return parts[1] ? { page: 'review', boardId: parts[1] } : { page: 'review' };
    case 'members': return { page: 'members' };
    case 'machines': return { page: 'machines' };
    case 'credits': return { page: 'credits' };
    case 'settings': return { page: 'settings' };
    case 'invite': return parts[1] ? { page: 'invite', code: parts[1] } : { page: 'kola', path: '/' };
    default: return { page: 'kola', path: '/' };
  }
}
export function pathFor(r: Route): string {
  switch (r.page) {
    case 'kola': return r.path === '/' ? '/boards' : r.path;
    case 'invite': return `/invite/${encodeURIComponent(r.code)}`;
    case 'review': return r.boardId ? `/review/${encodeURIComponent(r.boardId)}` : '/review';
    default: return `/${r.page}`;
  }
}

const listeners = new Set<() => void>();
export function navigate(path: string): void {
  if (window.location.pathname === path) return; // already there: nothing to re-render
  window.history.pushState(null, '', path);
  for (const l of listeners) l();
}
export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(window.location.pathname));
  useEffect(() => {
    const update = () => setRoute(parseRoute(window.location.pathname));
    listeners.add(update); window.addEventListener('popstate', update);
    return () => { listeners.delete(update); window.removeEventListener('popstate', update); };
  }, []);
  return route;
}
